import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'react-toastify';
import { whatsappAdminApi, type WahaLinkStatus } from '../api/whatsappApi';

type WahaLinkPanelProps = {
  channelId: string;
  onLinked?: () => void;
};

export function WahaLinkPanel({ channelId, onLinked }: WahaLinkPanelProps) {
  const [status, setStatus] = useState<WahaLinkStatus | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollStartedAtRef = useRef(0);

  const clearPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const refreshQr = useCallback(async () => {
    const blob = await whatsappAdminApi.getWahaQrBlob(channelId);
    if (!blob.type.startsWith('image/')) {
      throw new Error('No se pudo obtener el QR');
    }
    const reader = new FileReader();
    reader.onloadend = () => setQrDataUrl(String(reader.result));
    reader.readAsDataURL(blob);
  }, [channelId]);

  const refreshStatus = useCallback(async () => {
    const next = await whatsappAdminApi.getWahaStatus(channelId);
    setStatus(next);
    if (next.uiStatus === 'CONECTADO') {
      clearPoll();
      onLinked?.();
    }
    return next;
  }, [channelId, clearPoll, onLinked]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const next = await whatsappAdminApi.getWahaStatus(channelId);
        if (cancelled) return;
        setStatus(next);
        if (next.uiStatus === 'ESCANEAR_QR') {
          await refreshQr();
        }
      } catch (error) {
        if (!cancelled) {
          toast.error((error as Error).message || 'No se pudo cargar el estado de vinculación');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
      clearPoll();
    };
  }, [channelId, clearPoll, refreshQr]);

  useEffect(() => {
    if (status?.uiStatus !== 'ESCANEAR_QR') {
      clearPoll();
      pollStartedAtRef.current = 0;
      return;
    }
    pollStartedAtRef.current = Date.now();
    pollRef.current = setInterval(() => {
      if (Date.now() - pollStartedAtRef.current > 90_000) {
        clearPoll();
        toast.info('El QR venció. Volvé a tocar Vincular WhatsApp.');
        return;
      }
      refreshStatus().catch(() => undefined);
      refreshQr().catch(() => undefined);
    }, 2000);
    return clearPoll;
  }, [status?.uiStatus, clearPoll, refreshStatus, refreshQr]);

  const handleStart = async () => {
    setActionLoading(true);
    try {
      const next = await whatsappAdminApi.startWahaLink(channelId);
      setStatus(next);
      await refreshQr();
      toast.info('Escaneá el código QR con tu teléfono');
    } catch (error) {
      toast.error((error as Error).message || 'No se pudo iniciar la vinculación');
    } finally {
      setActionLoading(false);
    }
  };

  const handleLogout = async () => {
    const confirmed = window.confirm(
      'Van a dejar de llegar mensajes por este canal. Hay que volver a escanear el QR para reconectar.',
    );
    if (!confirmed) return;
    setActionLoading(true);
    try {
      const next = await whatsappAdminApi.logoutWahaLink(channelId);
      setStatus(next);
      setQrDataUrl(null);
      toast.success('WhatsApp desvinculado');
    } catch (error) {
      toast.error((error as Error).message || 'No se pudo desvincular');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return <p className='text-sm text-muted-foreground'>Cargando vinculación...</p>;
  }

  if (!status) {
    return null;
  }

  return (
    <div className='rounded-lg border p-4 space-y-3'>
      <div className='flex flex-wrap items-center justify-between gap-2'>
        <div>
          <p className='font-medium'>Vinculación WhatsApp</p>
          <p className='text-sm text-muted-foreground'>
            {status.uiStatus === 'CONECTADO'
              ? `Conectado · ${status.displayPhoneNumber}`
              : status.uiStatus === 'ESCANEAR_QR'
                ? 'Escaneá el QR con tu teléfono'
                : status.uiStatus === 'CAIDO'
                  ? 'Se desconectó. Volvé a vincular.'
                  : 'Sin vincular'}
          </p>
          {status.lastError && status.uiStatus === 'CAIDO' && (
            <p className='text-sm text-destructive mt-1'>{status.lastError}</p>
          )}
        </div>
        {status.uiStatus === 'SIN_VINCULAR' || status.uiStatus === 'CAIDO' ? (
          <Button onClick={handleStart} disabled={actionLoading}>
            Vincular WhatsApp
          </Button>
        ) : null}
        {status.uiStatus === 'CONECTADO' ? (
          <Button variant='outline' onClick={handleLogout} disabled={actionLoading}>
            Desvincular
          </Button>
        ) : null}
      </div>

      {status.uiStatus === 'ESCANEAR_QR' && qrDataUrl ? (
        <div className='flex flex-col items-center gap-2 py-2'>
          <img
            src={qrDataUrl}
            alt='Código QR para vincular WhatsApp en el teléfono'
            className='max-w-[280px] w-full h-auto border rounded-md'
          />
          <p className='text-xs text-muted-foreground text-center'>
            En el teléfono: WhatsApp → Dispositivos vinculados → Vincular dispositivo
          </p>
        </div>
      ) : null}
    </div>
  );
}
